import { createFileRoute } from '@tanstack/react-router';
import { Button } from '@osd/ui/button';
import { Card } from '@osd/ui/card';
import { Stack } from '@osd/ui/stack';
import { Tooltip } from '@osd/ui/tooltip';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/tooltip — live docs for the `Tooltip` primitive.
 *
 * Tooltip renders contextual help next to a trigger element. It
 * wires `aria-describedby` on the trigger and renders the surface
 * with `role="tooltip"`. Two API shapes:
 *
 *   1. Wrap-style (common):
 *      <Tooltip content="…">trigger</Tooltip>
 *
 *   2. Compound parts (when you need to lay the trigger out
 *      separately):
 *      <Tooltip.Trigger>trigger</Tooltip.Trigger>
 *      <Tooltip.Content side="top" align="center">…</Tooltip.Content>
 *      <Tooltip.Arrow side="top" />
 */
export const Route = createFileRoute('/ui/_layout/tooltip')({
  component: TooltipPage,
});

function TooltipPage() {
  return (
    <Stack gap={6}>
      <Section title="Tooltip" hint="Wrap-style API: <Tooltip content='…'>trigger</Tooltip>">
        <Stack direction="row" gap={4}>
          <Tooltip content="Copies the URL to your clipboard.">
            <Button>Copy</Button>
          </Tooltip>

          <Tooltip content="Admins can approve registrations, manage invites, and view audit logs.">
            <Button variant="ghost">What's an admin?</Button>
          </Tooltip>

          <Tooltip content="Status: account is fully provisioned." side="bottom">
            <Button variant="ghost">Status</Button>
          </Tooltip>
        </Stack>
      </Section>

      <Section
        title="Side + align"
        hint="Position the surface around the trigger (top | bottom | left | right)"
      >
        <Card>
          <Stack gap={3}>
            <Stack direction="row" gap={3}>
              <Tooltip content="left" side="left">
                <Button>← Left</Button>
              </Tooltip>
              <Tooltip content="top" side="top">
                <Button>↑ Top</Button>
              </Tooltip>
              <Tooltip content="right" side="right">
                <Button>Right →</Button>
              </Tooltip>
              <Tooltip content="bottom" side="bottom">
                <Button>↓ Bottom</Button>
              </Tooltip>
            </Stack>

            <Stack direction="row" gap={3}>
              <Tooltip content="start" side="top" align="start">
                <Button>align: start</Button>
              </Tooltip>
              <Tooltip content="center" side="top" align="center">
                <Button>align: center</Button>
              </Tooltip>
              <Tooltip content="end" side="top" align="end">
                <Button>align: end</Button>
              </Tooltip>
            </Stack>
          </Stack>
        </Card>
      </Section>

      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            language="tsx"
            title="Wrap-style"
            code={`<Tooltip content="Copies the URL">
  <Button>Copy</Button>
</Tooltip>
`}
          />

          <CodeBlock
            language="tsx"
            title="Compound parts (custom layout)"
            code={`<>
  <Tooltip.Trigger>trigger</Tooltip.Trigger>
  <Tooltip.Content side="top" align="center">
    Long-form help text with multiple lines if needed.
  </Tooltip.Content>
  <Tooltip.Arrow side="top" />
</>
`}
          />

          <CodeBlock
            language="tsx"
            title="Controlled"
            code={`const [open, setOpen] = useState(false);

<Tooltip content="hello" open={open}>
  <Button onClick={() => setOpen((v) => !v)}>toggle</Button>
</Tooltip>
`}
          />
        </Stack>
      </Section>
    </Stack>
  );
}
